import { formatDate } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, DashboardComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { nameForColumn } from '@aidkitorg/types/lib/translation/expr_to_sql';

/**
 * Checks if a string is a valid DateTime format.
 * Uses stricter criteria to avoid false positives with numeric strings like "1", "2", "3".
 */
export const isValidDateTime = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;

  // Must be at least 8 characters long (e.g., "1/1/2024" or "2024-1-1")
  if (str.length < 8) return false;

  // Must contain typical date separators or patterns (-, /, :, spaces, or month names)
  // This prevents pure numbers from being treated as dates
  const hasDatePattern = /[-\/:\s]|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i.test(str);
  if (!hasDatePattern) return false;

  // Finally check if it's actually parseable as a valid date
  const date = new Date(str);
  return date instanceof Date && !isNaN(date.getTime());
};

export type ProcessBarChartDataResult = {
  processedData: Record<string, any>[];
  primaryGroupField: string;
  summaryKey: string;
  valueKeys: string[];
  hasSecondaryGrouping: boolean;
};

/**
 * Shared bar chart data processing pipeline.
 * Implements: numeric conversion, null filtering, date sorting, secondary grouping pivot,
 * date formatting, and manual ordering.
 */
export function processBarChartData({
  component,
  queryResult,
}: {
  component: SupportedDashboardComponent & { kind: 'Bar Chart' };
  queryResult: DashboardComponentResult;
}): ProcessBarChartDataResult {
  const primaryGroupField = queryResult.groupKeys?.[0] || (component.groups[0].kind === 'Field' ? component.groups[0].field : '');
  const summaryKey = queryResult.summaryKey || '';

  // Extract bucket granularity for date formatting
  const bucketGroup = component.groups[0];
  const bucket = bucketGroup?.kind === 'Bucket Date' ? bucketGroup.bucket : undefined;

  // Check if we have grouped bars
  // Secondary groupings can be created in Distro in two ways: either by adding another X-Axis value, 
  // or by adding a "BarDisplay" grouped or stacked section.
  const barsConfig = (component as any).bars;
  const hasSecondaryGrouping = !!(barsConfig && barsConfig.values?.length > 0) || (queryResult.groupKeys?.length ?? 0) > 1;

  // Check if the primary group field contains DateTime values
  // Use the first non-null value to avoid skipping formatting when nulls appear first
  const sampleValue = queryResult.data.find((row: Record<string, any>) => row[primaryGroupField] != null)?.[primaryGroupField];
  const shouldFormatDateTime = sampleValue && isValidDateTime(String(sampleValue));

  // Convert string values to numbers for proper Y-axis scaling
  let processedData = queryResult.data.map((item: Record<string, any>) => ({
    ...item,
    [summaryKey]: Number(item[summaryKey])
  }));

  // Rename null/empty primary group values to "empty" so they are counted but clearly labeled
  processedData = processedData.map((item: Record<string, any>) => {
    const val = item[primaryGroupField];
    if (val == null || val === '' || String(val) === 'null') {
      return { ...item, [primaryGroupField]: 'empty' };
    }
    return item;
  });

  // Sort date-bucketed data chronologically BEFORE formatting,
  // so we sort on raw ISO strings rather than locale-formatted strings.
  if (!component.order && shouldFormatDateTime) {
    processedData = [...processedData].sort((a, b) =>
      new Date(a[primaryGroupField]).getTime() - new Date(b[primaryGroupField]).getTime()
    );
  }

  let valueKeys = [summaryKey]; // Default to original summaryKey for single group

  if (hasSecondaryGrouping) {
    const secondaryField = (barsConfig?.values?.length > 0 && nameForColumn(barsConfig.values[0])) 
      || queryResult.groupKeys?.[1] 
      || '';
    // Group data by primary field and pivot secondary field
    const groupedData: Record<string, Record<string, any>> = {};
    const secondaryValueSet = new Set<string>();

    processedData.forEach((row: any) => {
      const primaryValue = String(row[primaryGroupField]);
      // Format DateTime if detected
      const formattedPrimaryValue = shouldFormatDateTime
        ? formatDate(primaryValue, bucket || 'day')
        : primaryValue;
      const secondaryValue = String(row[secondaryField] || 'empty');
      const aggregateValue = Number(row[summaryKey]) || 0;

      if (!groupedData[formattedPrimaryValue]) {
        groupedData[formattedPrimaryValue] = { [primaryGroupField]: formattedPrimaryValue };
      }

      secondaryValueSet.add(secondaryValue);

      if (!groupedData[formattedPrimaryValue][secondaryValue]) {
        groupedData[formattedPrimaryValue][secondaryValue] = 0;
      }
      groupedData[formattedPrimaryValue][secondaryValue] += aggregateValue;
    });

    processedData = Object.values(groupedData);
    valueKeys = Array.from(secondaryValueSet);
  } else if (shouldFormatDateTime) {
    // Format DateTime for single series data
    processedData = processedData.map((item: Record<string, any>) => ({
      ...item,
      [primaryGroupField]: formatDate(String(item[primaryGroupField]), bucket || 'day'),
    }));
  }

  // Apply sorting based on order field
  if (component.order) {
    processedData = [...processedData].sort((a, b) => {
      let aVal: number, bVal: number;
      if (hasSecondaryGrouping) {
        // After pivot, summaryKey doesn't exist; sum all secondary value keys
        aVal = valueKeys.reduce((sum, k) => sum + (Number(a[k]) || 0), 0);
        bVal = valueKeys.reduce((sum, k) => sum + (Number(b[k]) || 0), 0);
      } else {
        aVal = Number(a[summaryKey]) || 0;
        bVal = Number(b[summaryKey]) || 0;
      }
      return component.order === 'ascending' ? aVal - bVal : bVal - aVal;
    });
  }

  return {
    processedData,
    primaryGroupField,
    summaryKey,
    valueKeys,
    hasSecondaryGrouping,
  };
}
